import {
  FilterState,
  FilterGroup,
  FilterCondition,
  FilterOperator,
  FieldDefinition,
  getFieldsForEntity,
  EntityType,
} from "./FilterBuilder.types";

export const generateId = (): string => {
  return `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const createEmptyCondition = (): FilterCondition => {
  return {
    id: generateId(),
    field: "",
    operator: "equals",
    value: "",
  };
};

export const createEmptyGroup = (): FilterGroup => {
  return {
    id: generateId(),
    logic: "AND",
    conditions: [createEmptyCondition()],
    groups: [],
  };
};

export const createEmptyFilterState = (): FilterState => {
  return {
    rootGroup: createEmptyGroup(),
  };
};

const convertValue = (value: string | number | boolean | string[] | null, fieldType: string): any => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (Array.isArray(value)) {
    return value;
  }

  switch (fieldType) {
    case "number":
      const num = Number(value);
      return isNaN(num) ? null : num;
    case "boolean":
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        return value.toLowerCase() === "true" || value === "1";
      }
      return Boolean(value);
    case "date":
      if (typeof value === "string") {
        // Return ISO date string, MongoDB will handle conversion
        return new Date(value).toISOString();
      }
      return value;
    default:
      return value;
  }
};

const convertConditionToMongoDB = (
  condition: FilterCondition,
  fields: FieldDefinition[]
): any => {
  const fieldDef = fields.find((f) => f.key === condition.field);
  if (!fieldDef) {
    return null;
  }

  const fieldPath = condition.field;
  const operator = condition.operator;
  const value = condition.value;

  // Handle null checks
  if (operator === "isNull") {
    return { [fieldPath]: null };
  }
  if (operator === "isNotNull") {
    return { [fieldPath]: { $ne: null } };
  }

  // Handle boolean fields
  if (fieldDef.type === "boolean") {
    const boolValue = convertValue(value, "boolean");
    if (operator === "equals") {
      return { [fieldPath]: boolValue };
    }
    if (operator === "notEquals") {
      return { [fieldPath]: { $ne: boolValue } };
    }
  }

  // Handle string operations
  if (fieldDef.type === "string") {
    const stringValue = String(value || "");
    switch (operator) {
      case "equals":
        return { [fieldPath]: stringValue };
      case "notEquals":
        return { [fieldPath]: { $ne: stringValue } };
      case "contains":
        return { [fieldPath]: { $regex: stringValue, $options: "i" } };
      case "notContains":
        return { [fieldPath]: { $not: { $regex: stringValue, $options: "i" } } };
      case "regexMatch":
        return { [fieldPath]: { $regex: stringValue } };
      default:
        return null;
    }
  }

  // Handle number operations
  if (fieldDef.type === "number") {
    const numValue = convertValue(value, "number");
    if (numValue === null) return null;

    switch (operator) {
      case "equals":
        return { [fieldPath]: numValue };
      case "notEquals":
        return { [fieldPath]: { $ne: numValue } };
      case "greaterThan":
        return { [fieldPath]: { $gt: numValue } };
      case "lessThan":
        return { [fieldPath]: { $lt: numValue } };
      case "greaterOrEqual":
        return { [fieldPath]: { $gte: numValue } };
      case "lessOrEqual":
        return { [fieldPath]: { $lte: numValue } };
      default:
        return null;
    }
  }

  // Handle date operations
  if (fieldDef.type === "date") {
    const dateValue = convertValue(value, "date");
    if (dateValue === null) return null;
    const date = new Date(dateValue);

    switch (operator) {
      case "equals":
        // For date equality, use range for the day
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        return {
          [fieldPath]: {
            $gte: startOfDay.toISOString(),
            $lte: endOfDay.toISOString(),
          },
        };
      case "notEquals":
        const startOfDayForNotEquals = new Date(date);
        startOfDayForNotEquals.setHours(0, 0, 0, 0);
        const endOfDayForNotEquals = new Date(date);
        endOfDayForNotEquals.setHours(23, 59, 59, 999);
        return {
          $or: [
            { [fieldPath]: { $lt: startOfDayForNotEquals.toISOString() } },
            { [fieldPath]: { $gt: endOfDayForNotEquals.toISOString() } },
          ],
        };
      case "greaterThan":
        return { [fieldPath]: { $gt: date.toISOString() } };
      case "lessThan":
        return { [fieldPath]: { $lt: date.toISOString() } };
      case "greaterOrEqual":
        return { [fieldPath]: { $gte: date.toISOString() } };
      case "lessOrEqual":
        return { [fieldPath]: { $lte: date.toISOString() } };
      default:
        return null;
    }
  }

  // Handle enum and list operations
  if (fieldDef.type === "enum" || fieldDef.type === "string") {
    if (operator === "inList" || operator === "notInList") {
      const listValue = Array.isArray(value) ? value : (value ? [value] : []);
      if (listValue.length === 0) return null;
      return {
        [fieldPath]: operator === "inList" ? { $in: listValue } : { $nin: listValue },
      };
    }
  }

  return null;
};

const convertGroupToMongoDB = (
  group: FilterGroup,
  fields: FieldDefinition[]
): any => {
  const mongoConditions: any[] = [];

  // Convert conditions
  group.conditions.forEach((condition) => {
    if (condition.field) {
      const mongoCondition = convertConditionToMongoDB(condition, fields);
      // Ensure it's a valid object with at least one key
      if (
        mongoCondition &&
        typeof mongoCondition === "object" &&
        !Array.isArray(mongoCondition) &&
        Object.keys(mongoCondition).length > 0
      ) {
        mongoConditions.push(mongoCondition);
      }
    }
  });

  // Convert nested groups
  group.groups.forEach((nestedGroup) => {
    const mongoGroup = convertGroupToMongoDB(nestedGroup, fields);
    if (
      mongoGroup &&
      typeof mongoGroup === "object" &&
      !Array.isArray(mongoGroup) &&
      Object.keys(mongoGroup).length > 0
    ) {
      mongoConditions.push(mongoGroup);
    }
  });

  if (mongoConditions.length === 0) {
    return null;
  }

  if (mongoConditions.length === 1) {
    return mongoConditions[0];
  }

  // Use the group's logic operator
  const operator = group.logic === "AND" ? "$and" : "$or";
  return { [operator]: mongoConditions };
};

const validateAndCleanMongoQuery = (query: any): any => {
  if (!query || typeof query !== "object" || Array.isArray(query)) {
    return null;
  }

  // If it has $and or $or, validate all items in the array
  if (query.$and && Array.isArray(query.$and)) {
    const cleanedAnd = query.$and
      .map((item: any) => {
        // First validate the item is a valid object
        if (!item || typeof item !== "object" || Array.isArray(item) || Object.keys(item).length === 0) {
          return null;
        }
        // Only recursively clean if the item itself contains $and or $or
        // Otherwise, it's a simple condition object and should be returned as-is
        if (item.$and || item.$or) {
          return validateAndCleanMongoQuery(item);
        }
        return item;
      })
      .filter((item: any) => item !== null && item !== undefined);
    
    if (cleanedAnd.length === 0) return null;
    if (cleanedAnd.length === 1) return cleanedAnd[0];
    return { $and: cleanedAnd };
  }

  if (query.$or && Array.isArray(query.$or)) {
    const cleanedOr = query.$or
      .map((item: any) => {
        // First validate the item is a valid object
        if (!item || typeof item !== "object" || Array.isArray(item) || Object.keys(item).length === 0) {
          return null;
        }
        // Only recursively clean if the item itself contains $and or $or
        // Otherwise, it's a simple condition object and should be returned as-is
        if (item.$and || item.$or) {
          return validateAndCleanMongoQuery(item);
        }
        return item;
      })
      .filter((item: any) => item !== null && item !== undefined);
    
    if (cleanedOr.length === 0) return null;
    if (cleanedOr.length === 1) return cleanedOr[0];
    return { $or: cleanedOr };
  }

  return query;
};

export const convertFilterToMongoDB = (
  filterState: FilterState,
  entityType: EntityType
): string | null => {
  if (!filterState || !filterState.rootGroup) {
    return null;
  }

  const fields = getFieldsForEntity(entityType);
  const mongoQuery = convertGroupToMongoDB(filterState.rootGroup, fields);

  if (!mongoQuery || Object.keys(mongoQuery).length === 0) {
    return null;
  }

  // Validate and clean the query
  const cleanedQuery = validateAndCleanMongoQuery(mongoQuery);
  if (!cleanedQuery || Object.keys(cleanedQuery).length === 0) {
    return null;
  }

  // Ensure the query structure is valid before stringifying
  try {
    // Test that the query can be stringified and parsed correctly
    const testString = JSON.stringify(cleanedQuery);
    const testParsed = JSON.parse(testString);
    
    // Verify all items in $and/$or arrays are objects
    if (testParsed.$and && Array.isArray(testParsed.$and)) {
      const allValid = testParsed.$and.every((item: any) => 
        item && typeof item === "object" && !Array.isArray(item)
      );
      if (!allValid) {
        console.error("Invalid items in $and array:", testParsed.$and);
        return null;
      }
    }
    
    if (testParsed.$or && Array.isArray(testParsed.$or)) {
      const allValid = testParsed.$or.every((item: any) => 
        item && typeof item === "object" && !Array.isArray(item)
      );
      if (!allValid) {
        console.error("Invalid items in $or array:", testParsed.$or);
        return null;
      }
    }
    
    return testString;
  } catch (error) {
    console.error("Error stringifying MongoDB query:", error, cleanedQuery);
    return null;
  }
};

export const validateCondition = (
  condition: FilterCondition,
  fields: FieldDefinition[]
): { valid: boolean; error?: string } => {
  if (!condition.field) {
    return { valid: false, error: "Field is required" };
  }

  const fieldDef = fields.find((f) => f.key === condition.field);
  if (!fieldDef) {
    return { valid: false, error: "Invalid field" };
  }

  // Check if operator is valid for this field
  if (!fieldDef.operators.includes(condition.operator)) {
    return { valid: false, error: "Invalid operator for this field" };
  }

  // Validate value based on operator
  if (condition.operator === "isNull" || condition.operator === "isNotNull") {
    return { valid: true };
  }

  if (condition.operator === "inList" || condition.operator === "notInList") {
    if (!Array.isArray(condition.value) || condition.value.length === 0) {
      return { valid: false, error: "List must contain at least one value" };
    }
    return { valid: true };
  }

  if (condition.value === null || condition.value === undefined || condition.value === "") {
    return { valid: false, error: "Value is required" };
  }

  // Validate type-specific values
  if (fieldDef.type === "number") {
    const num = Number(condition.value);
    if (isNaN(num)) {
      return { valid: false, error: "Value must be a number" };
    }
  }

  if (fieldDef.type === "date") {
    const date = new Date(condition.value as string);
    if (isNaN(date.getTime())) {
      return { valid: false, error: "Value must be a valid date" };
    }
  }

  if (fieldDef.type === "boolean") {
    if (typeof condition.value !== "boolean" && condition.value !== "true" && condition.value !== "false" && condition.value !== "1" && condition.value !== "0") {
      return { valid: false, error: "Value must be true or false" };
    }
  }

  return { valid: true };
};

export const countActiveFilters = (filterState: FilterState): number => {
  let count = 0;

  const countInGroup = (group: FilterGroup): number => {
    let groupCount = 0;
    groupCount += group.conditions.filter((c) => c.field && c.value !== null && c.value !== "" && c.value !== undefined).length;
    group.groups.forEach((g) => {
      groupCount += countInGroup(g);
    });
    return groupCount;
  };

  return countInGroup(filterState.rootGroup);
};

